import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useJoyride } from 'contexts/joyride';
import { useStripe } from 'contexts/stripe';
import { useHistory } from 'react-router-dom';
import {
    Button,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    Paper,
    ProgressBar,
    Skeleton,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { EXAMPLE_ASSIGNMENT_ID } from 'utils/constants';
import { canAccessFeature, isAssignmentValid as validateAssignment } from 'utils/helper';

const FEATURE = 'batch-evaluate-assignment-submissions';

const PaywallIconPrimary = (
    <Icon
        sx={(theme) => ({
            borderRadius: '50%',
            padding: theme.spacing(0.5),
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.primary[500],
        })}
        kind={'starFilled'}
    />
);

const PaywallIconWarning = (
    <Icon
        sx={(theme) => ({
            borderRadius: '50%',
            padding: theme.spacing(0.6),
            background: theme.palette.ensightful.warning[800],
            color: theme.palette.ensightful.shades[0],
        })}
        kind={'starFilled'}
    />
);

const GradesAndFeedback = ({ assignment, submissions, onClickViewSubmissions, onClickUploadSubmissions, onSelect }) => {
    const { batch, customer, entitlements } = useApp();
    const { currentUser } = useAuth();
    const history = useHistory();
    const { performNext } = useJoyride();
    const { hitPaywall } = useStripe();

    const isAssignmentValid = validateAssignment(assignment);

    const canEvaluateSubmissions = canAccessFeature(FEATURE, currentUser, customer, entitlements);

    if (assignment.id === EXAMPLE_ASSIGNMENT_ID) {
        if (!submissions?.length) {
            return (
                <Paper variant={'outlined'}>
                    <Stack divider={<Divider />} spacing={4}>
                        <Stack alignItems={'flex-start'} spacing={2}>
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                                <IconButton
                                    aria-label={'more options'}
                                    disabled
                                    kind={'moreHorizontal'}
                                    size={'small'}
                                    variant={'square'}
                                />
                            </Stack>
                            <Button disabled variant={'borderless'}>
                                View Submissions
                            </Button>
                        </Stack>
                        <Stack spacing={3}>
                            <Button
                                className={'joyride-step-5'}
                                sx={{ width: '100%' }}
                                disabled={!submissions}
                                loading={!submissions}
                                startIcon={!submissions ? undefined : <Icon kind={'upload'} />}
                                onClick={performNext}
                            >
                                Upload Submissions
                            </Button>
                            <Stack spacing={2}>
                                <Text color={'light'}>
                                    Grades and feedback will be generated immediately after you upload. Please note,
                                    editing assignment details after upload is restricted to ensure consistent
                                    grades/feedback for all students. If you need to make changes, duplicate the
                                    assignment and edit the new version.
                                </Text>
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
            );
        } else {
            return (
                <Paper variant={'outlined'}>
                    <Stack divider={<Divider />} spacing={4}>
                        <Stack alignItems={'flex-start'} spacing={2}>
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                                <Dropdown
                                    id={'grades-and-feedback-dropdown'}
                                    options={[]}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            disabled
                                            kind={'moreHorizontal'}
                                            size={'small'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={onSelect}
                                />
                            </Stack>
                            <Button variant={'borderless'} onClick={onClickViewSubmissions}>
                                View Submissions
                            </Button>
                        </Stack>
                        <Stack spacing={4}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <CustomIcon kind={'error'} />
                                    <Text>1 item to review</Text>
                                </Stack>
                                <Text color={'light'}>0/1 reviewed</Text>
                            </Stack>
                            <Button className={'joyride-step-6'} onClick={performNext}>
                                Review Submissions
                            </Button>
                            <Button startIcon={<Icon kind={'upload'} />} variant={'borderless'} onClick={() => {}}>
                                Upload Additional Submissions
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            );
        }
    }

    // not done fetching yet
    if (!submissions || !batch) {
        return (
            <Paper variant={'outlined'}>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Skeleton sx={{ fontSize: '1rem' }} variant={'rounded'} width={160} />
                            <Skeleton variant={'rounded'} width={24} height={24} />
                        </Stack>
                        <Skeleton width={100} />
                    </Stack>
                    <Stack spacing={3}>
                        <Skeleton variant={'rounded'} height={36} />
                        <Stack spacing={2}>
                            <Skeleton />
                            <Skeleton />
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    // upload in progress
    if (batch.length) {
        const numCompleted = submissions.filter(({ batch_id }) => batch_id === batch[0].id).length;
        const numTotal = batch[0].files.length;

        return (
            <Paper variant={'outlined'}>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack alignItems={'flex-start'} spacing={2}>
                        <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                            <IconButton
                                aria-label={'more options'}
                                disabled
                                kind={'moreHorizontal'}
                                size={'small'}
                                variant={'square'}
                            />
                        </Stack>
                        <Button disabled variant={'borderless'}>
                            View Submissions
                        </Button>
                    </Stack>
                    <Stack spacing={4}>
                        <Button
                            disabled
                            loading
                            loadingGrow
                            startIcon={<Icon kind={'upload'} />}
                            onClick={onClickUploadSubmissions}
                        >
                            Upload in Progress
                        </Button>
                        <Stack spacing={2}>
                            <Text color={'light'}>
                                {numCompleted}/{numTotal} assignments
                            </Text>
                            <ProgressBar
                                aria-label={'submission process progress'}
                                value={Number(((numCompleted / numTotal) * 100).toFixed(0))}
                            />
                        </Stack>
                        <Stack spacing={2}>
                            <Text color={'light'}>
                                Grades and feedback will be generated immediately after you upload. Please note, editing
                                assignment details after upload is restricted to ensure consistent grades/feedback for
                                all students. If you need to make changes, duplicate the assignment and edit the new
                                version.
                            </Text>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    // no submissions made yet
    if (!submissions.length) {
        return (
            <Paper variant={'outlined'}>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack alignItems={'flex-start'} spacing={2}>
                        <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                            <IconButton
                                aria-label={'more options'}
                                disabled
                                kind={'moreHorizontal'}
                                size={'small'}
                                variant={'square'}
                            />
                        </Stack>
                        <Button disabled variant={'borderless'}>
                            View Submissions
                        </Button>
                    </Stack>
                    <Stack spacing={3}>
                        <Button
                            sx={{ width: '100%' }}
                            disabled={
                                !isAssignmentValid ||
                                (!assignment.ai_grading_enabled && !assignment.ai_feedback_enabled)
                            }
                            startIcon={canEvaluateSubmissions ? <Icon kind={'upload'} /> : PaywallIconPrimary}
                            tooltip={
                                !assignment.ai_grading_enabled && !assignment.ai_feedback_enabled
                                    ? {
                                          style: { width: '100%' },
                                          component: 'Enable grading and/or feedback to upload submissions.',
                                      }
                                    : undefined
                            }
                            onClick={canEvaluateSubmissions ? onClickUploadSubmissions : hitPaywall}
                        >
                            Upload Submissions
                        </Button>
                        <Stack spacing={2}>
                            <Text color={'light'}>
                                Grades and feedback will be generated immediately after you upload. Please note, editing
                                assignment details after upload is restricted to ensure consistent grades/feedback for
                                all students. If you need to make changes, duplicate the assignment and edit the new
                                version.
                            </Text>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    // one or more submissions need to be reviewed
    if (submissions.some((submission) => !submission.grading)) {
        const numPending = submissions.filter(
            (submission) => (submission.ai || submission.is_failed) && !submission.grading
        ).length;
        const numReviewed = submissions.filter(
            (submission) => (submission.ai || submission.is_failed) && submission.grading
        ).length;
        const numTotal = submissions.filter((submission) => submission.ai || submission.is_failed).length;

        return (
            <Paper variant={'outlined'}>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack alignItems={'flex-start'} spacing={2}>
                        <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                            <Dropdown
                                id={'grades-and-feedback-dropdown'}
                                options={[
                                    {
                                        key: 'csv',
                                        value: 'Export Grades/Feedback',
                                        disabled: submissions.every(({ grading }) => !grading),
                                    },
                                    { key: 'delete', value: 'Reset Grading/Feedback' },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'small'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        </Stack>
                        <Button variant={'borderless'} onClick={onClickViewSubmissions}>
                            View Submissions
                        </Button>
                    </Stack>
                    <Stack spacing={4}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <CustomIcon kind={'error'} />
                                <Text>{numPending} items to review</Text>
                            </Stack>
                            <Text color={'light'}>
                                {numReviewed}/{numTotal} reviewed
                            </Text>
                        </Stack>
                        <Button onClick={() => history.push(`/assignments/${assignment.id}/grading`)}>
                            Review Submissions
                        </Button>
                        <Button
                            disabled={
                                !isAssignmentValid ||
                                (!assignment.ai_grading_enabled && !assignment.ai_feedback_enabled)
                            }
                            startIcon={canEvaluateSubmissions ? <Icon kind={'upload'} /> : PaywallIconWarning}
                            tooltip={
                                !assignment.ai_grading_enabled && !assignment.ai_feedback_enabled
                                    ? {
                                          style: { alignSelf: 'center' },
                                          component: 'Enable grading and/or feedback to upload submissions.',
                                      }
                                    : undefined
                            }
                            variant={'borderless'}
                            onClick={canEvaluateSubmissions ? onClickUploadSubmissions : hitPaywall}
                        >
                            Upload Additional Submissions
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    // all submissions have been reviewed
    if (submissions.every((submission) => submission.grading)) {
        const numReviewed = submissions.filter(({ ai, grading, is_failed }) => (ai || is_failed) && grading).length;

        return (
            <Paper variant={'outlined'}>
                <Stack divider={<Divider />} spacing={4}>
                    <Stack alignItems={'flex-start'} spacing={2}>
                        <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p1'}>Final Submission Grading and Feedback</Text>
                            <Dropdown
                                id={'grades-and-feedback-dropdown'}
                                options={[
                                    {
                                        key: 'csv',
                                        value: 'Export Grades/Feedback',
                                        disabled: submissions.every(({ grading }) => !grading),
                                    },
                                    { key: 'delete', value: 'Reset Grading/Feedback' },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'small'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        </Stack>
                        <Button variant={'borderless'} onClick={onClickViewSubmissions}>
                            View Submissions
                        </Button>
                    </Stack>
                    <Stack spacing={4}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <CustomIcon kind={'error'} />
                                <Text>0 items to review</Text>
                            </Stack>
                            <Text color={'light'}>
                                {numReviewed}/{numReviewed} reviewed
                            </Text>
                        </Stack>
                        <Button disabled>Review Submissions</Button>
                        <Button
                            disabled={
                                !isAssignmentValid ||
                                (!assignment.ai_grading_enabled && !assignment.ai_feedback_enabled)
                            }
                            startIcon={<Icon kind={'upload'} />}
                            tooltip={
                                !assignment.ai_grading_enabled && !assignment.ai_feedback_enabled
                                    ? {
                                          style: { alignSelf: 'center' },
                                          component: 'Enable grading and/or feedback to upload submissions.',
                                      }
                                    : undefined
                            }
                            variant={'borderless'}
                            onClick={canEvaluateSubmissions ? onClickUploadSubmissions : hitPaywall}
                        >
                            Upload Additional Submissions
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    // should never get here
    return null;
};

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        padding: theme.spacing(0.75),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

export default GradesAndFeedback;
